import {
  TOAST_SIZE,
  TOAST_TYPES,
  ToastSize,
  ToastType,
} from '@/core/types/enums';
import { Icon } from './Icon';
import { iCloseCircle } from '@pte/react-icons';
import { staticUrl } from '@/core/utils';
import { useEffect, useState } from 'react';

const typeStyles: Record<ToastType, { icon: string }> = {
  [TOAST_TYPES.SUCCESS]: {
    icon: '/icons/featured-success-outline-lg.svg',
  },
  [TOAST_TYPES.WARNING]: {
    icon: '/icons/featured-warning-outline-lg.svg',
  },
  [TOAST_TYPES.ERROR]: {
    icon: '/icons/featured-error-outline-lg.svg',
  },
};

const widthStyles: Record<ToastSize, string> = {
  [TOAST_SIZE.SMALL]: 'w-[343px]',
  [TOAST_SIZE.MEDIUM]: 'w-[480px]',
};

interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  description: string;
  onClose: (id: string) => void;
  duration?: number;
  size?: ToastSize;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  description,
  onClose,
  duration = 5000,
  size = TOAST_SIZE.SMALL,
}) => {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const cusStyle = typeStyles[type] || typeStyles.success;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), duration - 400); // fade-out trước khi remove
    const removeTimer = setTimeout(() => onClose(id), duration);
    return () => {
      clearTimeout(timer);
      clearTimeout(removeTimer);
    };
  }, [id, onClose, duration]);

  useEffect(() => {
    const interval = 50; // Update every 50ms for smooth animation
    const increment = (100 / duration) * interval;
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + increment;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, interval);

    return () => clearInterval(progressTimer);
  }, [duration]);

  return (
    <div
      id={`toast-${id}`}
      className={`relative flex items-start gap-3 bg-white shadow-100 rounded-md px-4 py-3 ${widthStyles[size]} border border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
        visible
          ? 'animate-toast-in opacity-100 translate-y-0'
          : 'animate-toast-out opacity-0 -translate-y-3'
      }`}
      data-pte-name='toast'
    >
      <div data-pte-name='toast-icon-wrapper'>
        <img
          src={staticUrl(cusStyle.icon || '')}
          alt={type}
          width={28}
          height={28}
          data-pte-name='toast-icon'
        />
      </div>
      <div className='flex-1' data-pte-name='toast-content'>
        <h4 className='font-semibold text-sm' data-pte-name='toast-title'>
          {title}
        </h4>
        <p className='text-gray-500 text-xs' data-pte-name='toast-description'>
          {description}
        </p>
      </div>
      <button
        onClick={() => onClose(id)}
        className='text-gray-400 hover:text-gray-600'
        data-pte-name='toast-close-btn'
      >
        <Icon icon={iCloseCircle} className='w-4 h-4' />
      </button>
      {/* Progress bar */}
      <div
        className='absolute bottom-0 left-0 h-1 bg-orange-500 transition-all ease-linear'
        style={{ width: `${progress}%` }}
        data-pte-name='toast-progress-bar'
      />
    </div>
  );
};
